import { SyntheticEvent, useEffect, useState } from 'react';
import { Autocomplete as MuiAutocomplete, TextField } from '@mui/material';
import { CoreTypography } from '@youscience/core';
import { isEmpty } from 'lodash-es';

import { STATES_OF_USA } from '@utils/states';
import { AutocompleteOption } from '@interfaces';
import { renderNoOptionsText } from '@components/Form/Autocomplete/Autocomplete';
import useEducationPlannerStore from '@store/EducationPlannerStore';

import { sxStyles, StyledStep1Wrap } from './SelectionStep1Planner.styles';

interface SelectionStep1Props {
  isShowHelperText: boolean;
}

export const SelectionStep1 = (props: SelectionStep1Props) => {
  const { isShowHelperText } = props;

  let stateInputRef: HTMLInputElement | null = null;
  let schoolInputRef: HTMLInputElement | null = null;

  const { state, schoolListOptions, schoolPreviewInfo } = useEducationPlannerStore(
    (state) => state.schoolSelectionData,
  );

  const getSchoolListByState = useEducationPlannerStore((state) => state.getSchoolListByState);
  const getSchoolInfo = useEducationPlannerStore((state) => state.getSchoolInfo);
  const setSchoolSelectionData = useEducationPlannerStore((state) => state.setSchoolSelectionData);

  const selectedSchool = isEmpty(schoolPreviewInfo)
    ? null
    : {
        label: `${schoolPreviewInfo?.name} (${schoolPreviewInfo?.zip}, ${schoolPreviewInfo?.city})`,
        value: schoolPreviewInfo?.id.toString(),
      };

  const [school, setSchool] = useState(selectedSchool);

  const handleSelectState = (event: SyntheticEvent, state: AutocompleteOption | null) => {
    void setSchoolSelectionData({ state, schoolListOptions: [] });

    setSchool(null);
  };

  const handleSelectEnrolledSchool = async (event: SyntheticEvent, school: AutocompleteOption | null) => {
    if (!school) return;

    void setSchoolSelectionData({
      school: {
        label: school?.label.split('(')[0].trim(),
        value: school?.value,
      },
    });

    setSchool(school);

    void setSchoolSelectionData({ deadlineType: undefined, priority: 1 });

    await getSchoolInfo();
  };

  useEffect(() => {
    void getSchoolListByState();
  }, [state]);

  useEffect(() => {
    stateInputRef?.focus();
  }, []);

  useEffect(() => {
    if (state && schoolListOptions && schoolListOptions.length > 0) {
      schoolInputRef?.focus();
    }
  }, [state, schoolListOptions]);

  return (
    <StyledStep1Wrap>
      <CoreTypography data-testid="step1Subtitle" sx={sxStyles.subtitle} variant="body2">
        That’s a huge step in your life. Find your school by selecting the state then typing the name of your school.
      </CoreTypography>

      <MuiAutocomplete
        data-testid="enrolledStatesAutocomplete"
        size="small"
        options={STATES_OF_USA}
        value={state}
        onChange={handleSelectState}
        renderInput={(params) => (
          <TextField
            {...params}
            label="State"
            inputRef={(input) => {
              stateInputRef = input as HTMLInputElement;
            }}
          />
        )}
        clearIcon={null}
        noOptionsText={renderNoOptionsText()}
      />

      <MuiAutocomplete
        data-testid="schoolSearchAutocomplete"
        sx={sxStyles.schoolSearch}
        size="small"
        value={school}
        isOptionEqualToValue={(option, school) => option.value === school.value}
        options={schoolListOptions || []}
        disabled={!state || !schoolListOptions || schoolListOptions.length < 1}
        onChange={handleSelectEnrolledSchool}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Educational Institution Name"
            inputRef={(input) => {
              schoolInputRef = input as HTMLInputElement;
            }}
            error={isShowHelperText}
            helperText={isShowHelperText && 'The school is already selected. Please select another one.'}
          />
        )}
        clearIcon={null}
        noOptionsText={renderNoOptionsText()}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.label} {option.value}
          </li>
        )}
      />
    </StyledStep1Wrap>
  );
};
